import Cookies from 'universal-cookie'
import { refreshAccessToken } from '../graphql/mutations/refreshAccessToken'
import { REFRESH_TOKEN_EXPIRED } from '../strings'

/**
 * The refresh half of the session both applications share.
 *
 * The backend DELETES a refresh token the moment it is spent and answers an
 * unknown one with 401 — so two contexts refreshing the same pair is fatal for
 * whichever loses, and this app shares its cookie jar with Orchid, which
 * already refreshes under the same rules. Three properties keep that safe:
 *
 * - the refresh token is read from the jar AT THE MOMENT OF USE, never from a
 *   value captured earlier (the captured copy is stale the moment anyone else
 *   refreshes);
 * - a pair another context already published is ADOPTED rather than re-spent —
 *   detected by the access token in the jar no longer being the one that was
 *   refused;
 * - refreshes are serialised (navigator.locks where the browser has it, a
 *   module promise otherwise, both under the same lock name Orchid takes), so
 *   concurrent 401s produce one refresh, not N.
 *
 * Failure is classified, because the caller's reaction differs by a session:
 * a REFUSED refresh means the backend looked at the token and said no — the
 * session is over, clearing the jar is right. A refresh that merely failed to
 * reach the server says nothing about the session, and clearing the shared jar
 * for weather signs the user out of BOTH applications.
 */

const AUTH_TOKEN_COOKIE = 'AUTH_TOKEN'
const REFRESH_TOKEN_COOKIE = 'REFRESH_TOKEN'
const REFRESH_LOCK_NAME = 'nodafi-auth-refresh'
const SESSION_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000

const REFUSED = Symbol('refresh-refused')

interface SharedPair {
  token: string
  refreshToken: string
}

interface RefusedError extends Error {
  [REFUSED]: true
}

function refused(message: string): RefusedError {
  const error = new Error(message) as RefusedError
  error[REFUSED] = true
  return error
}

/** Whether the backend itself refused the refresh — the one case that ends a session. */
export function isRefreshRefused(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as Partial<RefusedError>)[REFUSED] === true
  )
}

/*
 * The same cookie library the rest of the app writes these with.
 *
 * `useAuthToken` sets them through react-cookie, and the Apollo client already
 * reads them through universal-cookie (react-cookie's non-hook half) in
 * useConfiguredApolloClient. Going through it here too means the attributes and
 * the encoding match those writes by construction rather than by inspection —
 * a raw `document.cookie` write does not URL-encode, so a reader and a writer
 * that disagree about encoding corrupt the value for any token that is not
 * already URL-safe.
 *
 * A hook is not usable here: this runs inside the Apollo error link, outside
 * React.
 */
const cookies = new Cookies()

function readSharedPair(): SharedPair | null {
  const token: unknown = cookies.get(AUTH_TOKEN_COOKIE)
  const refreshToken: unknown = cookies.get(REFRESH_TOKEN_COOKIE)
  if (typeof token !== 'string' || token === '') return null
  if (typeof refreshToken !== 'string' || refreshToken === '') return null
  return { token, refreshToken }
}

function writeSharedPair(pair: SharedPair): void {
  const expires = new Date(Date.now() + SESSION_LIFETIME_MS)
  cookies.set(AUTH_TOKEN_COOKIE, pair.token, { path: '/', expires })
  cookies.set(REFRESH_TOKEN_COOKIE, pair.refreshToken, { path: '/', expires })
}

// Serialises within this tab even where navigator.locks is missing; the Web
// Lock is what extends that across tabs and across applications.
let tabLocalChain: Promise<unknown> = Promise.resolve()

async function withRefreshLock<T>(attempt: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (locks !== undefined) {
    // async so a synchronous throw from locks.request becomes a rejection the
    // caller can classify, rather than escaping unclassified into onError.
    return locks.request(REFRESH_LOCK_NAME, attempt)
  }
  const next = tabLocalChain.then(attempt, attempt)
  tabLocalChain = next.catch(() => undefined)
  return next
}

interface RefreshSharedSessionInput {
  /** The access token whose rejection prompted this refresh. */
  spentAccessToken: string | null | undefined
  graphQLEndpoint: string
}

function runRefreshSharedSession({
  spentAccessToken,
  graphQLEndpoint,
}: RefreshSharedSessionInput): Promise<SharedPair> {
  return withRefreshLock(async () => {
    const current = readSharedPair()
    if (current === null) {
      throw refused('No refresh token available')
    }
    if (
      spentAccessToken !== null &&
      spentAccessToken !== undefined &&
      current.token !== spentAccessToken
    ) {
      // Someone else already refreshed and published; our refresh token is
      // destroyed. Their pair is the session now.
      return current
    }

    let pair: SharedPair
    try {
      pair = await refreshAccessToken(current.refreshToken, graphQLEndpoint)
    } catch (error) {
      if (error instanceof Error && error.message === REFRESH_TOKEN_EXPIRED) {
        throw refused(error.message)
      }
      throw error
    }
    writeSharedPair(pair)
    return pair
  })
}

// Intra-tab single-flight: many operations can 401 at once, and each would
// otherwise refresh in turn under the lock — the later ones spending a token
// the first already rotated. Collapse them into one, as Orchid's client does.
let inFlight: Promise<SharedPair> | null = null

export function refreshSharedSession(input: RefreshSharedSessionInput): Promise<SharedPair> {
  if (inFlight !== null) return inFlight
  inFlight = runRefreshSharedSession(input).finally(() => {
    inFlight = null
  })
  return inFlight
}
